// static/table_engine_src/core/selection/visuals.js

export function installSelectionVisuals(
  ctx,
  {
    table,
    cols,
    rowEls,
    colIndexByKey,
    keyForColIndex,
    thForKey,
    tdAt,
    clampRC,
    scrollCellIntoView,
  } = {},
) {
  const allCols = Array.isArray(cols) ? cols : [];
  let _gridId = "";
  let _lastNavCell = null;

  function announce(msg) {
    try { ctx.announceSelectionA11y?.(msg); } catch {}
  }

  function rowCount() {
    const rows = rowEls();
    return Array.isArray(rows) ? rows.length : 0;
  }

  function visibleDataColIndices() {
    const hiddenCols = ctx?.hiddenCols;
    const out = [];
    for (let i = 0; i < allCols.length; i += 1) {
      const col = allCols[i];
      const key = String(col?.key || "");
      const kind = String(col?.kind || "").toLowerCase();
      const isHidden =
        !!col?.hidden ||
        !!(hiddenCols && typeof hiddenCols.has === "function" && key && hiddenCols.has(key));
      if (isHidden) continue;
      if (kind === "rowhandle" || kind === "actions") continue;
      out.push(i);
    }
    return out.length ? out : allCols.map((_, i) => i);
  }

  function colOrdinal(cIdx) {
    const visible = visibleDataColIndices();
    const total = Math.max(visible.length, 1);
    const at = visible.indexOf(cIdx);
    if (at >= 0) return { index: at + 1, total };
    const fallback = Math.max(1, Math.min(total, cIdx + 1));
    return { index: fallback, total };
  }

  function colLabel(cIdx) {
    const c = allCols[cIdx];
    if (!c) return `column ${cIdx + 1}`;
    return String(c.header || c.key || `column ${cIdx + 1}`);
  }

  function ensureGridId() {
    if (_gridId) return _gridId;
    const fromTable = String(table.id || "").trim();
    if (fromTable) {
      _gridId = fromTable;
      return _gridId;
    }
    _gridId = `te-grid-${Math.random().toString(36).slice(2, 10)}`;
    return _gridId;
  }

  function ensureCellId(td, tr, cIdx) {
    if (!td) return "";
    if (td.id) return td.id;
    const r0 = parseInt(tr?.dataset?.teRowIdx || "-1", 10);
    const rowIdx = Number.isInteger(r0) && r0 >= 0 ? r0 : 0;
    const c0 = parseInt(td?.dataset?.colIdx || String(cIdx), 10);
    const colIdx = Number.isInteger(c0) && c0 >= 0 ? c0 : Math.max(0, cIdx);
    const id = `${ensureGridId()}-r${rowIdx}-c${colIdx}`;
    td.id = id;
    return id;
  }

  function clearNavCell(el = _lastNavCell) {
    if (!el) return;
    try { delete el.dataset?.teNavCell; } catch {}
    try { el.removeAttribute?.("data-te-nav-cell"); } catch {}
    try { el.tabIndex = -1; } catch {}
  }

  function setTableTabStop(hasNavCell) {
    if (!table) return;
    try { table.tabIndex = hasNavCell ? -1 : 0; } catch {}
  }

  function setNavCell(el) {
    if (_lastNavCell && _lastNavCell !== el) clearNavCell(_lastNavCell);
    if (!el) {
      _lastNavCell = null;
      setTableTabStop(false);
      return;
    }
    _lastNavCell = el;
    try { el.dataset.teNavCell = "1"; } catch {}
    try { el.setAttribute("data-te-nav-cell", "1"); } catch {}
    try { el.tabIndex = 0; } catch {}
    setTableTabStop(true);
  }

  function clearMarksInRoot() {
    table.querySelectorAll(".te-selected, .te-range, .te-active, .te-copy-source").forEach((el) => {
      el.classList.remove("te-selected", "te-range", "te-active", "te-copy-source");
    });

    table.querySelectorAll("[data-te-selected], [data-te-range], [data-te-active], [data-te-copy-source], [data-te-fill-handle-cell]").forEach((el) => {
      delete el.dataset.teSelected;
      delete el.dataset.teRange;
      delete el.dataset.teActive;
      delete el.dataset.teCopySource;
      delete el.dataset.teRangeIndex;
      delete el.dataset.teFillHandleCell;
    });
    table.querySelectorAll("[data-te-col-selected]").forEach((el) => {
      delete el.dataset.teColSelected;
    });
    table.querySelectorAll("[data-te-nav-cell]").forEach((el) => clearNavCell(el));
    table.querySelectorAll("[aria-selected]").forEach((el) => el.removeAttribute("aria-selected"));
    clearNavCell(_lastNavCell);
    _lastNavCell = null;
    setTableTabStop(false);
    table.removeAttribute("aria-activedescendant");
  }

  function markSelected(el) {
    if (!el) return;
    el.classList.add("te-selected");
    el.dataset.teSelected = "1";
    el.setAttribute("aria-selected", "true");
  }

  function markRange(el) {
    if (!el) return;
    el.classList.add("te-range");
    el.dataset.teRange = "1";
    el.setAttribute("aria-selected", "true");
  }

  function normalizeRange(range) {
    if (!range || typeof range !== "object") return null;
    const r0 = Number.isInteger(range.r0) ? range.r0 : Number(range.r0);
    const r1 = Number.isInteger(range.r1) ? range.r1 : Number(range.r1);
    const c0 = Number.isInteger(range.c0) ? range.c0 : Number(range.c0);
    const c1 = Number.isInteger(range.c1) ? range.c1 : Number(range.c1);
    if (![r0, r1, c0, c1].every(Number.isFinite)) return null;

    const lo = clampRC(Math.min(r0, r1), Math.min(c0, c1));
    const hi = clampRC(Math.max(r0, r1), Math.max(c0, c1));
    return {
      r0: Math.min(lo.r, hi.r),
      r1: Math.max(lo.r, hi.r),
      c0: Math.min(lo.c, hi.c),
      c1: Math.max(lo.c, hi.c),
    };
  }

  function rangeKey(rg) {
    return `${rg.r0}:${rg.r1}:${rg.c0}:${rg.c1}`;
  }

  function normalizeCellRanges(ranges) {
    const out = [];
    const seen = new Set();
    for (const rgRaw of Array.isArray(ranges) ? ranges : []) {
      const rg = normalizeRange(rgRaw);
      if (!rg) continue;
      const key = rangeKey(rg);
      if (seen.has(key)) continue;
      seen.add(key);
      out.push(rg);
    }
    return out;
  }

  function markActive(el) {
    if (!el) return;
    el.classList.add("te-active", "te-selected");
    el.dataset.teActive = "1";
    el.dataset.teSelected = "1";
    el.setAttribute("aria-selected", "true");
  }

  function markCopySource(el) {
    if (!el) return;
    el.classList.add("te-copy-source");
    el.dataset.teCopySource = "1";
  }

  function isCopyableVisibleCol(col) {
    if (!col?.key) return false;
    if (ctx.hiddenCols?.has?.(col.key)) return false;
    if (col.kind === "rowHandle" || col.kind === "actions") return false;
    return col.kind === "input" || col.kind === "output";
  }

  function normalizeCopySnapshot(raw) {
    if (!raw || typeof raw !== "object") return null;
    const kind = String(raw.kind || "");

    if (kind === "ranges") {
      const ranges = normalizeCellRanges(raw.ranges || []);
      return ranges.length ? { kind: "ranges", ranges } : null;
    }

    if (kind === "cols") {
      const colKeys = [];
      const seen = new Set();
      for (const keyRaw of Array.isArray(raw.colKeys) ? raw.colKeys : []) {
        const key = String(keyRaw || "");
        if (!key || seen.has(key)) continue;
        seen.add(key);
        colKeys.push(key);
      }
      return colKeys.length ? { kind: "cols", colKeys } : null;
    }

    if (kind === "rows") {
      const rowIdxs = [];
      const seen = new Set();
      for (const rRaw of Array.isArray(raw.rowIdxs) ? raw.rowIdxs : []) {
        const r = Number(rRaw);
        if (!Number.isInteger(r) || r < 0 || seen.has(r)) continue;
        seen.add(r);
        rowIdxs.push(r);
      }
      rowIdxs.sort((a, b) => a - b);
      return rowIdxs.length ? { kind: "rows", rowIdxs } : null;
    }

    return null;
  }

  function copySnapshotFromSelectionState() {
    const s = ctx._sel || {};

    const rangesInState = normalizeCellRanges([
      ...(Array.isArray(s.cellRanges) ? s.cellRanges : []),
      ...(s.cellRange ? [s.cellRange] : []),
    ]);
    if (rangesInState.length) return { kind: "ranges", ranges: rangesInState };

    if (s.selectedCols?.size) {
      const colKeys = allCols
        .map((c) => c?.key)
        .filter(Boolean)
        .filter((k) => s.selectedCols.has(k))
        .filter((k) => isCopyableVisibleCol(ctx.colByKey?.get?.(k)));
      if (colKeys.length) return { kind: "cols", colKeys };
    }

    if (s.selectedRows?.size) {
      const rowIdxs = [...s.selectedRows].filter((r) => Number.isInteger(r)).sort((a, b) => a - b);
      if (rowIdxs.length) return { kind: "rows", rowIdxs };
    }

    const single = s.cellAnchor || s.cellFocus || null;
    if (single) {
      const cIdx = Number(single.c);
      const col = allCols[cIdx];
      if (Number.isInteger(cIdx) && isCopyableVisibleCol(col)) {
        const rg = normalizeRange({ r0: single.r, r1: single.r, c0: cIdx, c1: cIdx });
        if (rg) return { kind: "ranges", ranges: [rg] };
      }
    }

    return null;
  }

  function copySnapshotKey(snapshot) {
    const s = normalizeCopySnapshot(snapshot);
    if (!s) return "";
    if (s.kind === "ranges") {
      return `ranges:${(s.ranges || [])
        .map((rg) => `${rg.r0}:${rg.r1}:${rg.c0}:${rg.c1}`)
        .join("|")}`;
    }
    if (s.kind === "cols") return `cols:${(s.colKeys || []).join("|")}`;
    if (s.kind === "rows") return `rows:${(s.rowIdxs || []).join("|")}`;
    return "";
  }

  function setCopySourceFromSelection() {
    const next = normalizeCopySnapshot(copySnapshotFromSelectionState());
    const prev = normalizeCopySnapshot(ctx._teCopySnapshot);
    if (copySnapshotKey(prev) === copySnapshotKey(next)) return;
    ctx._teCopySnapshot = next;
    applySelectionVisuals();
  }

  function clearCopySourceVisual({ repaint = true } = {}) {
    if (!ctx._teCopySnapshot) return;
    ctx._teCopySnapshot = null;
    if (repaint) applySelectionVisuals();
  }

  function paintCopySnapshot(snapshot, { rows, paintedRows, rowByIdx }) {
    if (!snapshot) return;

    if (snapshot.kind === "ranges") {
      for (const rg of snapshot.ranges || []) {
        for (let r = rg.r0; r <= rg.r1; r += 1) {
          const tr = rowByIdx.get(r) || rows[r];
          if (!isMountedNode(tr)) continue;
          for (let c = rg.c0; c <= rg.c1; c += 1) {
            if (!isCopyableVisibleCol(allCols[c])) continue;
            markCopySource(tdAt(tr, c));
          }
        }
      }
      return;
    }

    if (snapshot.kind === "cols") {
      for (const key of snapshot.colKeys || []) {
        const cIdx = colIndexByKey(key);
        if (cIdx < 0 || !isCopyableVisibleCol(allCols[cIdx])) continue;
        const th =
          thForKey(key) ||
          table.querySelector(`thead th[data-col-idx="${String(cIdx)}"]`) ||
          table.querySelector(`thead tr:first-child > th:nth-child(${String(cIdx + 1)})`);
        markCopySource(th);
        for (const tr of paintedRows) {
          if (!isMountedNode(tr)) continue;
          markCopySource(tdAt(tr, cIdx));
        }
      }
      return;
    }

    if (snapshot.kind === "rows") {
      for (const r of snapshot.rowIdxs || []) {
        const tr = rowByIdx.get(r) || rows[r];
        if (!isMountedNode(tr)) continue;
        for (let c = 0; c < allCols.length; c += 1) {
          if (!isCopyableVisibleCol(allCols[c])) continue;
          markCopySource(tdAt(tr, c));
        }
      }
    }
  }

  function isMountedNode(el) {
    if (!el) return false;
    if (typeof el.isConnected === "boolean") return el.isConnected;
    return true;
  }

  function applySelectionVisuals() {
    const s = ctx._sel;
    if (!s) return;

    clearMarksInRoot();

    const rows = rowEls();
    const paintedRows =
      (typeof ctx.getRenderedRowEls === "function" ? ctx.getRenderedRowEls() : null) ||
      rows;

    const rowByIdx = new Map();
    paintedRows.forEach((tr, i) => {
      if (!isMountedNode(tr)) return;
      const idx0 = parseInt(tr?.dataset?.teRowIdx || "", 10);
      const idx = Number.isInteger(idx0) && idx0 >= 0 ? idx0 : i;
      rowByIdx.set(idx, tr);
    });

    if (s.selectedCols && s.selectedCols.size) {
      for (const colKey of s.selectedCols) {
        const cIdx = colIndexByKey(colKey);
        if (cIdx < 0) continue;
        const th =
          thForKey(colKey) ||
          table.querySelector(`thead th[data-col-idx="${String(cIdx)}"]`) ||
          table.querySelector(`thead tr:first-child > th:nth-child(${String(cIdx + 1)})`);
        markSelected(th);
        if (th?.dataset) th.dataset.teColSelected = "1";
        for (const tr of paintedRows) {
          if (!isMountedNode(tr)) continue;
          markSelected(tdAt(tr, cIdx));
        }
      }
    }

    if (s.selectedRows && s.selectedRows.size) {
      for (const r of s.selectedRows) {
        const tr = rowByIdx.get(r) || rows[r];
        if (!isMountedNode(tr)) continue;
        [...tr.children].forEach((td) => markSelected(td));
      }
    }

    const extraRanges = normalizeCellRanges(s.cellRanges || []);
    const activeRange = normalizeRange(s.cellRange);
    const rangesToPaint = [...extraRanges, ...(activeRange ? [activeRange] : [])];
    s.cellRanges = extraRanges;
    s.cellRange = activeRange;

    for (let ri = 0; ri < rangesToPaint.length; ri += 1) {
      const { r0, r1, c0, c1 } = rangesToPaint[ri];
      for (let r = r0; r <= r1; r++) {
        const tr = rowByIdx.get(r) || rows[r];
        if (!isMountedNode(tr)) continue;
        for (let c = c0; c <= c1; c++) {
          const cell = tdAt(tr, c);
          markRange(cell);
          if (cell?.dataset) cell.dataset.teRangeIndex = String(ri);
        }
      }

      // If the range covers full row extent, treat it as column-like and mark headers too.
      const rowCount = rows.length;
      const fullHeight = rowCount > 0 && r0 <= 0 && r1 >= (rowCount - 1);
      if (fullHeight) {
        for (let c = c0; c <= c1; c++) {
          const key = keyForColIndex(c);
          const th =
            (key ? thForKey(key) : null) ||
            table.querySelector(`thead th[data-col-idx="${String(c)}"]`) ||
            table.querySelector(`thead tr:first-child > th:nth-child(${String(c + 1)})`);
          markSelected(th);
          if (th?.dataset) th.dataset.teColSelected = "1";
        }
      }
    }

    let navCell = null;
    const navRef = s.cellFocus || s.cellAnchor || null;
    if (navRef) {
      const { r, c } = navRef;
      const tr = rowByIdx.get(r) || rows[r];
      if (isMountedNode(tr)) {
        const td = tdAt(tr, c);
        markActive(td);
        navCell = td;
        const id = ensureCellId(td, tr, c);
        if (id) table.setAttribute("aria-activedescendant", id);
      }
    }
    // Fill-handle target:
    // - For a range: bottom-right corner of active range
    // - For a single-cell anchor/focus: active/nav cell itself
    const fillHandleTarget = activeRange
      ? { r: activeRange.r1, c: activeRange.c1 }
      : navRef
        ? { r: navRef.r, c: navRef.c }
        : null;

    if (fillHandleTarget) {
      const tr = rowByIdx.get(fillHandleTarget.r) || rows[fillHandleTarget.r];
      if (isMountedNode(tr)) {
        const td = tdAt(tr, fillHandleTarget.c);
        if (td?.dataset) td.dataset.teFillHandleCell = "1";
      }
    }

    const copySnapshot = normalizeCopySnapshot(ctx._teCopySnapshot);
    ctx._teCopySnapshot = copySnapshot;
    paintCopySnapshot(copySnapshot, { rows, paintedRows, rowByIdx });

    setNavCell(navCell);
  }

  function clearSelectionVisuals() {
    clearMarksInRoot();
  }

  function setSelectedColumns(keys, { anchorIdx = null } = {}) {
    const s = ctx._sel;

    s.selectedCols = new Set((keys || []).filter(Boolean));
    s.colAnchorIdx = Number.isInteger(anchorIdx) ? anchorIdx : null;

    s.selectedRows.clear();
    s.rowAnchorIdx = null;
    s.cellAnchor = null;
    s.cellFocus = null;
    s.cellRange = null;
    s.cellRanges = [];

    applySelectionVisuals();
    const n = s.selectedCols.size;
    if (n === 1) {
      const only = [...s.selectedCols][0];
      const idx = colIndexByKey(only);
      const pos = colOrdinal(idx);
      announce(`Column selected: column ${pos.index} of ${pos.total}, ${colLabel(idx)}.`);
    } else {
      announce(`${n} columns selected.`);
    }
  }

  function setSelectedRows(rowIdxs, { anchorIdx = null } = {}) {
    const s = ctx._sel;

    const rows = rowEls();
    const n = rows.length;

    const set = new Set();
    (rowIdxs || []).forEach((r) => {
      if (!Number.isInteger(r)) return;
      if (r < 0 || r >= n) return;
      set.add(r);
    });

    s.selectedRows = set;
    s.rowAnchorIdx = Number.isInteger(anchorIdx) ? anchorIdx : null;

    s.selectedCols.clear();
    s.colAnchorIdx = null;
    s.cellAnchor = null;
    s.cellFocus = null;
    s.cellRange = null;
    s.cellRanges = [];

    applySelectionVisuals();
    const nSel = set.size;
    if (nSel === 1) {
      const only = [...set][0];
      const total = Math.max(1, rowCount());
      announce(`Row selected: row ${only + 1} of ${total}.`);
    } else {
      announce(`${nSel} rows selected.`);
    }
  }

  function setCellAnchor(r, c, { preserveRanges = false } = {}) {
    const s = ctx._sel;

    s.selectedCols.clear();
    s.selectedRows.clear();
    s.colAnchorIdx = null;
    s.rowAnchorIdx = null;

    const rc = clampRC(r, c);
    s.cellAnchor = { r: rc.r, c: rc.c };
    s.cellFocus = { r: rc.r, c: rc.c };
    s.cellRange = null;
    if (!preserveRanges) s.cellRanges = [];

    applySelectionVisuals();
    scrollCellIntoView(rc.r, rc.c);
    const totalRows = Math.max(1, rowCount());
    const pos = colOrdinal(rc.c);
    announce(`Cell selected: row ${rc.r + 1} of ${totalRows}, column ${pos.index} of ${pos.total}, ${colLabel(rc.c)}.`);
  }

  function setCellRangeFromAnchor(r, c, { append = false, anchor = null } = {}) {
    const s = ctx._sel;
    const rc = clampRC(r, c);

    const aRaw = anchor && Number.isInteger(anchor.r) && Number.isInteger(anchor.c)
      ? clampRC(anchor.r, anchor.c)
      : (s.cellAnchor || rc);
    const a = { r: aRaw.r, c: aRaw.c };

    const nextRange = normalizeRange({
      r0: Math.min(a.r, rc.r),
      r1: Math.max(a.r, rc.r),
      c0: Math.min(a.c, rc.c),
      c1: Math.max(a.c, rc.c),
    });
    if (!nextRange) {
      setCellAnchor(rc.r, rc.c);
      return;
    }

    if (append) {
      const baseline = [
        ...(Array.isArray(s.cellRanges) ? s.cellRanges : []),
        ...(s.cellRange ? [s.cellRange] : []),
      ];
      s.cellRanges = normalizeCellRanges(baseline);
    } else {
      s.cellRanges = [];
    }

    s.cellAnchor = { r: a.r, c: a.c };
    s.cellRange = nextRange;
    s.cellFocus = { r: rc.r, c: rc.c };

    applySelectionVisuals();
    scrollCellIntoView(rc.r, rc.c);
    const nRows = nextRange.r1 - nextRange.r0 + 1;
    const nCols = nextRange.c1 - nextRange.c0 + 1;
    const multi = (Array.isArray(s.cellRanges) ? s.cellRanges.length : 0) + 1;
    if (multi > 1) {
      announce(`Range selected: ${nRows} row${nRows === 1 ? "" : "s"} by ${nCols} column${nCols === 1 ? "" : "s"}. ${multi} ranges selected.`);
    } else {
      announce(`Range selected: ${nRows} row${nRows === 1 ? "" : "s"} by ${nCols} column${nCols === 1 ? "" : "s"}.`);
    }
  }

  function selectColumnByClick(colKey, e) {
    const s = ctx._sel;
    const idx = colIndexByKey(colKey);
    if (idx < 0) return;

    const isToggle = !!(e && (e.ctrlKey || e.metaKey));
    const isRange = !!(e && e.shiftKey);

    if (isRange && Number.isInteger(s.colAnchorIdx)) {
      const a = s.colAnchorIdx;
      const lo = Math.min(a, idx);
      const hi = Math.max(a, idx);
      const keys = [];
      for (let i = lo; i <= hi; i++) {
        const k = keyForColIndex(i);
        if (k) keys.push(k);
      }
      setSelectedColumns(keys, { anchorIdx: a });
      return;
    }

    if (isToggle) {
      const next = new Set(s.selectedCols);
      if (next.has(colKey)) next.delete(colKey);
      else next.add(colKey);
      const anchor = Number.isInteger(s.colAnchorIdx) ? s.colAnchorIdx : idx;
      setSelectedColumns([...next], { anchorIdx: anchor });
      return;
    }

    setSelectedColumns([colKey], { anchorIdx: idx });
  }

  function selectRowByClick(r, e) {
    const s = ctx._sel;
    const rows = rowEls();
    if (!Number.isInteger(r) || r < 0 || r >= rows.length) return;

    const isToggle = !!(e && (e.ctrlKey || e.metaKey));
    const isRange = !!(e && e.shiftKey);

    if (isRange && Number.isInteger(s.rowAnchorIdx)) {
      const a = s.rowAnchorIdx;
      const lo = Math.min(a, r);
      const hi = Math.max(a, r);
      const idxs = [];
      for (let i = lo; i <= hi; i++) idxs.push(i);
      setSelectedRows(idxs, { anchorIdx: a });
      return;
    }

    if (isToggle) {
      const next = new Set(s.selectedRows);
      if (next.has(r)) next.delete(r);
      else next.add(r);
      const anchor = Number.isInteger(s.rowAnchorIdx) ? s.rowAnchorIdx : r;
      setSelectedRows([...next], { anchorIdx: anchor });
      return;
    }

    setSelectedRows([r], { anchorIdx: r });
  }

  function getSelection() {
    const s = ctx._sel;
    const rows = rowEls();
    const nRows = rows.length;

    if (s.cellRange) return { ...s.cellRange };

    if (s.cellAnchor) {
      const { r, c } = s.cellAnchor;
      return { r0: r, r1: r, c0: c, c1: c };
    }

    if (s.cellFocus) {
      const { r, c } = s.cellFocus;
      return { r0: r, r1: r, c0: c, c1: c };
    }

    if (s.selectedCols && s.selectedCols.size) {
      const idxs = [...s.selectedCols].map(colIndexByKey).filter((x) => x >= 0);
      if (!idxs.length || nRows === 0) return null;
      return { r0: 0, r1: nRows - 1, c0: Math.min(...idxs), c1: Math.max(...idxs) };
    }

    if (s.selectedRows && s.selectedRows.size) {
      const idxs = [...s.selectedRows].filter((x) => Number.isInteger(x));
      if (!idxs.length) return null;
      return { r0: Math.min(...idxs), r1: Math.max(...idxs), c0: 0, c1: allCols.length - 1 };
    }

    return null;
  }

  function getSelections() {
    const s = ctx._sel;
    const active = getSelection();
    const extras = normalizeCellRanges(s.cellRanges || []);
    if (!active) return extras;

    const out = [];
    const seen = new Set();
    for (const rg of [...extras, active]) {
      const n = normalizeRange(rg);
      if (!n) continue;
      const key = rangeKey(n);
      if (seen.has(key)) continue;
      seen.add(key);
      out.push(n);
    }
    return out;
  }

  function getPasteStart() {
    const s = ctx._sel;
    if (s.cellRange) return { r: s.cellRange.r0, c: s.cellRange.c0 };
    if (s.cellAnchor) return { r: s.cellAnchor.r, c: s.cellAnchor.c };
    if (s.cellFocus) return { r: s.cellFocus.r, c: s.cellFocus.c };
    if (s.selectedCols && s.selectedCols.size) {
      const idxs = [...s.selectedCols].map(colIndexByKey).filter((x) => x >= 0);
      return { r: 0, c: idxs.length ? Math.min(...idxs) : 0 };
    }
    return { r: 0, c: 0 };
  }

  ctx.captureCopySourceFromSelection = setCopySourceFromSelection;
  ctx.clearCopySourceVisual = clearCopySourceVisual;

  return {
    applySelectionVisuals,
    clearSelectionVisuals,
    setSelectedColumns,
    setSelectedRows,
    setCellAnchor,
    setCellRangeFromAnchor,
    selectColumnByClick,
    selectRowByClick,
    getSelection,
    getSelections,
    getPasteStart,
    setCopySourceFromSelection,
    clearCopySourceVisual,
  };
}
